import { isValidElement, type ReactNode, type FC, type Ref } from "react";
import { isString, mergeClasses } from "@huub-web/utils";

import { Card, type CardProps } from "../Card/Card";
import type { UserPromptRecord, ValueType } from "../../types";
import { Flex } from "../Flex/Flex";
import { Text } from "../Text/Text";
import { Tooltip } from "../Tooltip/Tooltip";
import { AskAITooltipContent } from "../AskAITooltipContent/AskAITooltipContent";
import { IconButton } from "../IconButton/IconButton";
import { ValueParser } from "../ValueParser/ValueParser";
import type { ClassNameProp } from "../../props";
import {
  cardStyle,
  iconButtonFooterStyle,
  iconButtonHeaderStyle,
  titleContainerStyle,
  titleStyle,
  valueContainerStyle,
  flexGrowStyle,
} from "./ExpenseCard.css";

export type ExpenseCardProps = ClassNameProp &
  Pick<
    CardProps,
    "padding" | "border" | "variant" | "overflowHidden" | "children"
  > & {
    title: ReactNode;
    body?: ReactNode;
    titleColor?: "default" | "accent" | "success";
    value: ValueType | ReactNode | null;
    disabled?: boolean;
    helpPrompt?: UserPromptRecord;
    align?: "center" | "left";
    valueGrow?: boolean;
    onHelpPrompt?: () => void;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

export const ExpenseCard: FC<ExpenseCardProps> = ({
  title,
  helpPrompt,
  titleColor = "default",
  value,
  body,
  disabled,
  variant = "primary",
  align = "left",
  valueGrow = true,
  onHelpPrompt,
  border = "default",
  children,
  className,
  ref,
  ...props
}) => (
  <Card
    ref={ref}
    gap="xs"
    border={border}
    variant={variant}
    className={mergeClasses(className)}
    contentClassName={cardStyle}
    {...props}
  >
    {(!!title || helpPrompt) && (
      <Flex
        gap="m"
        align="center"
        justify={align === "center" ? "center" : "space-between"}
        className={titleContainerStyle}
      >
        {isString(title) ? (
          <Text
            className={titleStyle({ color: titleColor })}
            align={align === "center" ? "center" : undefined}
          >
            {title}
          </Text>
        ) : (
          title
        )}

        {helpPrompt && (
          <Tooltip
            value={<AskAITooltipContent prompt={helpPrompt} />}
            placement="bottom"
            disabled={disabled}
          >
            <IconButton
              icon="aiFilled"
              variant="primary"
              size="xxs"
              rounded
              disabled={disabled}
              onClick={onHelpPrompt}
              className={iconButtonHeaderStyle}
            />
          </Tooltip>
        )}
      </Flex>
    )}

    <Flex
      gap="s"
      direction="column"
      className={valueContainerStyle}
      justify="space-between"
      grow
    >
      {value === null ? (
        <Flex grow={valueGrow} />
      ) : isValidElement(value) ? (
        value
      ) : (
        <Text
          variant="displayS"
          align={align === "center" ? "center" : undefined}
          className={valueGrow ? flexGrowStyle : undefined}
          bold
        >
          <ValueParser value={value as ValueType} />
        </Text>
      )}

      {body &&
        (isValidElement(body) ? (
          body
        ) : (
          <Text
            variant="bodyS"
            markdown
            align={align === "center" ? "center" : undefined}
          >
            {body}
          </Text>
        ))}

      {children}

      {helpPrompt && (
        <Tooltip
          value={<AskAITooltipContent prompt={helpPrompt} />}
          placement="bottom"
          disabled={disabled}
        >
          <IconButton
            icon="aiFilled"
            variant="primary"
            size="xxs"
            rounded
            disabled={disabled}
            onClick={onHelpPrompt}
            className={iconButtonFooterStyle}
          />
        </Tooltip>
      )}
    </Flex>
  </Card>
);
